import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { convertWidthToVW, leftRightAdapter } from "@/utils/adapter";
import { FONT_FAMILY } from "@/utils/font-family";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useTheme } from "@/contexts/theme";

const useStyle = () => {
  const { t } = useTranslation();
  const { neutralColor, primaryColor, successColor, secondColor, errorColor } =
    useGomakeTheme();
  const { currentTheme } = useTheme();
  const isHpClient = currentTheme === "hp-client";
  const classes = useMemo(() => {
    return {
      container: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        paddingBottom: 5,
        paddingTop: 5,
        backgroundColor: "var(--theme-bg-primary, #ffffff)",
        paddingLeft: 12,
        paddingRight: 12,
        marginBottom: isHpClient ? 0 : 20,
        borderBottom: "1px solid var(--theme-border-color, transparent)",
      },
      searchInputContainer: {
        width: convertWidthToVW(375),
        backgroundColor: "#F8F8F8",
        borderRadius: 10,
        height: 40,
        alignSelf: "flex-start",
        display: "flex",
      },
      iconStyle: {
        position: "absolute" as "absolute",
        ...leftRightAdapter(t("direction"), 16),
        top: 5,
      },
      rightSideContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "4px",
      },
      profileContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 14,
        // marginLeft: 10,
      },
      userNameStyle: {
        ...FONT_FAMILY.Lexend(400, 12),
        color: "#000",
      },
      mainMenuContainer: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        padding: "4px 6px",
        minWidth: 200,
      },
      mainMenuContainer2: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        width: "466px",
        gap: 10,
        overflow: "visible",
      },
      accountTextStyle: {
        width: "100%",
        ...FONT_FAMILY.Lexend(600, 10),
        padding: "14px 12px",
        color: neutralColor(500),
        textTransform: "uppercase" as "uppercase",
        letterSpacing: "0.5px",
      },
      notificationTextStyle: {
        width: "100%",
        ...FONT_FAMILY.Lexend(500, 14),
        padding: 10,
        paddingLeft: 16,
        paddingRight: 16,
        color: neutralColor(600),
      },
      footerTextStyle: {
        ...FONT_FAMILY.Lexend(400, 14),
        color: primaryColor(200),
      },
      imgNameContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        gap: 10,
        padding: "10px 12px",
        backgroundColor: neutralColor(100),
        borderRadius: "8px",
      },
      nameTextStyle: {
        ...FONT_FAMILY.Lexend(600, 13),
        color: neutralColor(800),
        lineHeight: "1.3",
      },
      emailTextStyle: {
        ...FONT_FAMILY.Lexend(400, 11),
        color: neutralColor(500),
        lineHeight: "1.3",
        marginTop: "2px",
      },
      manageAccountStyle: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        padding: "2px 0",
      },
      manageAccountTextStyle: {
        ...FONT_FAMILY.Lexend(500, 13),
        color: neutralColor(700),

      },
      logoutContainer: {
        width: "100%",
        ...FONT_FAMILY.Lexend(500, 13),
        color: errorColor(500),
        padding: "2px 0",
      },
      lineContainer: {
        display: "flex",
        width: "100%",
        height: 1,
        backgroundColor: neutralColor(200),
        margin: "4px 0",
      },
      avatarProps: {
        width: 36,
        height: 36,
        fontSize: "12px",
        fontWeight: 600,
      },
      menuItemTextStyle: {
        display: "flex",
        width: "100%",
        ...FONT_FAMILY.Lexend(400, 16),
        color: primaryColor(900),
        paddingTop: "10px",
        paddingLeft: "10px",
      },
      textStyle: {
        whiteSpace: "pre-wrap" as "pre-wrap", // Preserve newlines and spaces
        wordWrap: "break-word" as "break-word",
        ...FONT_FAMILY.Lexend(400, 16),
        color: primaryColor(900),
      },
      subTextStyle: {
        ...FONT_FAMILY.Lexend(400, 12),
        color: "#A5ACB8",
        padding: "0px 0px 0px 64px",
      },
      menuItemContainer: {
        ...FONT_FAMILY.Lexend(500, 14),
        color: neutralColor(600),
        display: "flex",
        alignItems: "flex-start",
      },
      footerItemContainer: {
        ...FONT_FAMILY.Lexend(500, 14),
        color: neutralColor(600),
        display: "flex",
        alignItems: "center",
        gap: "4px",
      },
      acceptBtnStyle: {
        width: "64px",
        height: "28px",
        backgroundColor: successColor(500),
      },
      rejectBtnStyle: {
        width: "64px",
        height: "28px",
        color: "#3C4257",
        borderColor: "#DDDEE1",
      },
      iconBtnContainer: {
        padding: "2px",
      },
      bookADemo: {
        width: "300px",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 16px",
        // backgroundColor: "#FFD700", // gold, change as you wish
        color: "#000", // black text
        borderRadius: "20px",
        textDecoration: "none",
        fontWeight: 600,
        fontSize: "14px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease",
      },
      verticalDivider: {
        width: "1px",
        height: "24px",
        backgroundColor: "#D0D5DD",
        margin: "0 8px",
      },
    };
  }, [i18next.language, t, isHpClient]);
  return {
    classes,
    primaryColor,
    secondColor,
    errorColor,
    neutralColor,
  };
};
export { useStyle };
